import { useCallback, useState } from "react";
import styled from "@emotion/styled";
import type { FC, KeyboardEvent, MouseEvent } from "react";
import {
  MDSIcon,
  MDSIconColor,
  MDSModalDialog,
  MDSButton,
  MDSButtonSlotName,
  MDSButtonGroup,
  MDSButtonVariant,
  MDSText,
  MDSTextVariant,
  MDSDialogTextContent,
  MDSTextField,
} from "@/mds";
import { MDSIconKind } from "@/mds/components/icon";
import { MDSDialogSlotName } from "@/mds/components/overlays/dialog/dialog.types";
import { api } from "@/modules/api";
import { logger } from "@/modules/logger";
import { trackEvent, TrackedEvent } from "@/domains/metrics";

type Props = {
  connectedCalendarEventId: string;
  markdownContent: string | null;
  noteIds: string[];
};

type FeedbackResponse = "UPVOTE" | "DOWNVOTE";

const postFeedback = async ({
  connectedCalendarEventId,
  response,
  comment,
}: {
  connectedCalendarEventId: string;
  response: FeedbackResponse;
  comment: string | null;
}): Promise<void> => {
  const feedbackResponse = await api.post(
    `/v2/connected-calendar-events/{calendar_event_id}/meeting-briefing/feedback`,
    {
      params: { path: { calendar_event_id: connectedCalendarEventId } },
      body: { response, comment },
    }
  );

  if (!feedbackResponse.data) {
    throw new Error("Failed to submit meeting briefing feedback");
  }
};

export const MeetingBriefingFeedback: FC<Props> = ({
  connectedCalendarEventId,
  markdownContent,
  noteIds,
}) => {
  const [submittedResponse, setSubmittedResponse] = useState<FeedbackResponse | null>(null);
  const [pendingResponse, setPendingResponse] = useState<FeedbackResponse | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [comment, setComment] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const stopProp = useCallback((event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
  }, []);

  const stopHeaderToggleKeyEvent = useCallback((event: KeyboardEvent<HTMLElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.stopPropagation();
    }
  }, []);

  const handleThumbClick = useCallback((response: FeedbackResponse) => {
    setPendingResponse(response);
    setComment("");
    setErrorMessage(null);
    setIsModalOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setIsModalOpen(false);
    setPendingResponse(null);
    setComment("");
    setErrorMessage(null);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!pendingResponse) return;
    setIsSubmitting(true);
    setErrorMessage(null);
    try {
      await postFeedback({
        connectedCalendarEventId,
        response: pendingResponse,
        comment: comment.trim() || null,
      });
      setSubmittedResponse(pendingResponse);
      trackEvent(TrackedEvent.MeetingBriefingFeedbackSubmit, {
        connected_calendar_event_id: connectedCalendarEventId,
        response: pendingResponse,
        comment: comment.trim() || null,
        note_ids: noteIds,
        markdown: markdownContent,
      });
      setIsModalOpen(false);
      setPendingResponse(null);
      setComment("");
    } catch (_err) {
      logger.error({ message: "Failed to submit meeting briefing feedback" });
      setErrorMessage("Could not submit feedback. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  }, [pendingResponse, comment, connectedCalendarEventId, noteIds, markdownContent]);

  if (submittedResponse) {
    return (
      <StyledFeedbackBadge onClick={stopProp}>
        <StyledLabsLabel>
          <MDSIcon kind={MDSIconKind.Flask} color={MDSIconColor.Inherit} size={9} boxSize={12} />
          Beta
        </StyledLabsLabel>
        <StyledDivider />
        <MDSIcon
          kind={submittedResponse === "UPVOTE" ? MDSIconKind.Upvote : MDSIconKind.Downvote}
          color={MDSIconColor.Inherit}
          size={10}
          boxSize={12}
        />
        <StyledThanksMessage>Thanks!</StyledThanksMessage>
      </StyledFeedbackBadge>
    );
  }

  return (
    <>
      <StyledFeedbackBadge onClick={stopProp}>
        <StyledLabsLabel>
          <MDSIcon kind={MDSIconKind.Flask} color={MDSIconColor.Inherit} size={9} boxSize={12} />
          Beta
        </StyledLabsLabel>
        <StyledDivider />
        <StyledFeedbackActionButton
          onClick={e => {
            e.stopPropagation();
            handleThumbClick("UPVOTE");
          }}
          onKeyDown={stopHeaderToggleKeyEvent}
          aria-label="Thumbs up"
        >
          <MDSIcon kind={MDSIconKind.Upvote} color={MDSIconColor.Inherit} size={10} boxSize={12} />
        </StyledFeedbackActionButton>
        <StyledFeedbackActionButton
          onClick={e => {
            e.stopPropagation();
            handleThumbClick("DOWNVOTE");
          }}
          onKeyDown={stopHeaderToggleKeyEvent}
          aria-label="Thumbs down"
        >
          <MDSIcon
            kind={MDSIconKind.Downvote}
            color={MDSIconColor.Inherit}
            size={10}
            boxSize={12}
          />
        </StyledFeedbackActionButton>
      </StyledFeedbackBadge>

      <MDSModalDialog isOpen={isModalOpen} handleClose={handleClose}>
        <MDSText slot={MDSDialogSlotName.Header} variant={MDSTextVariant.HeaderL}>
          Share feedback
        </MDSText>
        <MDSDialogTextContent slot={MDSDialogSlotName.Content}>
          <MDSText variant={MDSTextVariant.ParagraphS}>
            How useful was this meeting briefing?
          </MDSText>
          <StyledResponseButtons>
            <MDSButton
              variant={
                pendingResponse === "UPVOTE"
                  ? MDSButtonVariant.EmphasisLight
                  : MDSButtonVariant.Default
              }
              onClick={() => setPendingResponse("UPVOTE")}
              aria-pressed={pendingResponse === "UPVOTE"}
            >
              <MDSIcon slot={MDSButtonSlotName.LeftIcon} kind={MDSIconKind.Upvote} />
              Good
            </MDSButton>
            <MDSButton
              variant={
                pendingResponse === "DOWNVOTE"
                  ? MDSButtonVariant.DangerFilled
                  : MDSButtonVariant.Default
              }
              onClick={() => setPendingResponse("DOWNVOTE")}
              aria-pressed={pendingResponse === "DOWNVOTE"}
            >
              <MDSIcon slot={MDSButtonSlotName.LeftIcon} kind={MDSIconKind.Downvote} />
              Bad
            </MDSButton>
          </StyledResponseButtons>
          <MDSText variant={MDSTextVariant.ParagraphS}>
            {pendingResponse === "UPVOTE"
              ? "Glad this briefing was helpful! Any additional thoughts?"
              : "Sorry this briefing wasn't helpful. What could be improved?"}
          </MDSText>
          <StyledFeedbackTextArea
            value={comment}
            onChange={setComment}
            placeholder="Share more details"
            isTextArea={true}
            textAreaProps={{
              rows: 3,
              onKeyDown: stopHeaderToggleKeyEvent,
            }}
          />
          {errorMessage ? <StyledInlineError role="alert">{errorMessage}</StyledInlineError> : null}
        </MDSDialogTextContent>
        <MDSButtonGroup slot={MDSDialogSlotName.ButtonsRight}>
          <MDSButton onClick={handleClose}>Cancel</MDSButton>
          <MDSButton
            variant={MDSButtonVariant.Contrast}
            isLoading={isSubmitting}
            onClick={handleSubmit}
          >
            Submit
          </MDSButton>
        </MDSButtonGroup>
      </MDSModalDialog>
    </>
  );
};

const StyledFeedbackBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  margin-left: auto;
  border-radius: 4px;
  padding: 0 ${({ theme }) => theme.spacing["1"]};
  min-height: 22px;
  color: ${({ theme }) => theme.colors.text.emphasis};
  background: ${({ theme }) => theme.colors.background.badge.emphasis};
`;

const StyledLabsLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing["0.5"]};
  font-size: ${({ theme }) => theme.fontSizes["text-xs"]};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
`;

const StyledDivider = styled.span`
  width: 1px;
  height: 12px;
  margin: 0 2px;
  background: currentColor;
  opacity: 0.2;
`;

const StyledFeedbackActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.max};
  background: transparent;
  color: inherit;
  cursor: pointer;
  padding: 0;

  &:hover {
    background: ${({ theme }) => theme.colors.background.button.transparent.hover};
  }
`;

const StyledThanksMessage = styled.span`
  font-size: ${({ theme }) => theme.fontSizes["text-xs"]};
  color: inherit;
`;

const StyledResponseButtons = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing["2"]};
  margin: ${({ theme }) => theme.spacing["2"]} 0;
`;

const StyledFeedbackTextArea = styled(MDSTextField)`
  width: 100%;
  margin-top: ${({ theme }) => theme.spacing["2"]};
  min-height: 72px;
`;

const StyledInlineError = styled.div`
  margin-top: ${({ theme }) => theme.spacing["2"]};
  color: ${({ theme }) => theme.colors.text.danger};
  font-size: ${({ theme }) => theme.fontSizes["text-xs"]};
`;
